import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  'Mentor',
  'Teacher / Educator',
  'Storyteller / Writer',
  'Content Creator',
  'Community Moderator',
  'Editor',
  'Partnerships',
  'Career Opportunities',
  'General Interest'
];

const OPPORTUNITIES = [
  { title: 'Mentor', num: '01', desc: 'Guide and support other women on their journeys.' },
  { title: 'Teacher', num: '02', desc: 'Share your expertise through workshops and content.' },
  { title: 'Storyteller', num: '03', desc: 'Contribute authentic stories that inspire and inform.' },
  { title: 'Leader', num: '04', desc: 'Help build and lead initiatives across our community.' }
];

const Careers = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', country: '', interest: '', expertise: '', message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/careers/apply`, formData);
      toast.success('Thank you. Your message has been received.');
      setFormData({ name: '', email: '', country: '', interest: '', expertise: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-ivory min-h-screen" data-testid="careers-page">
      {/* Hero */}
      <section className="py-24 lg:py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center space-y-8">
          <p className="label-editorial">Join Behenchara</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-8xl leading-[0.95] text-charcoal" data-testid="careers-hero-title">
            Build the<br/><em className="text-burgundy not-italic">sisterhood</em> with us.
          </h1>
          <p className="text-lg md:text-xl text-charcoal-muted max-w-2xl mx-auto leading-relaxed">
            We are always open to connecting with passionate women who want to contribute to the movement — as mentors, teachers, storytellers, or leaders.
          </p>
        </div>
      </section>

      {/* Opportunities */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-t border-subtle">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-editorial mb-4">Where You Can Contribute</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-charcoal">Ways to shine.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-subtle">
            {OPPORTUNITIES.map((item, i) => (
              <div key={item.title} className="p-8 border border-subtle -m-px bg-white editorial-card" data-testid={`opportunity-${i}`}>
                <p className="font-serif text-5xl text-terracotta mb-6">{item.num}</p>
                <h3 className="font-serif text-2xl text-charcoal mb-3">{item.title}</h3>
                <p className="text-sm text-charcoal-muted leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current status note */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-ivory-warm border-t border-subtle">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <p className="label-editorial">Currently</p>
          <h3 className="font-serif text-3xl md:text-4xl text-charcoal">Always open to connect.</h3>
          <p className="text-charcoal-muted leading-relaxed">
            While we don't have specific role openings at this moment, we're actively building our founding team and community. If you feel called to be part of what we're creating, we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-t border-subtle">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="label-editorial mb-4">Tell Us About Yourself</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-charcoal">
              Let's begin<br/>the conversation.
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8" data-testid="careers-application-form">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="name" className="label-editorial block mb-3">Full Name *</label>
                <input
                  id="name" name="name" type="text" value={formData.name} onChange={handleChange} required
                  className="w-full px-0 py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
                  data-testid="careers-form-name-input"
                />
              </div>
              <div>
                <label htmlFor="email" className="label-editorial block mb-3">Email *</label>
                <input
                  id="email" name="email" type="email" value={formData.email} onChange={handleChange} required
                  className="w-full px-0 py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
                  data-testid="careers-form-email-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="country" className="label-editorial block mb-3">Country</label>
                <input
                  id="country" name="country" type="text" value={formData.country} onChange={handleChange}
                  className="w-full px-0 py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
                  data-testid="careers-form-country-input"
                />
              </div>
              <div>
                <label htmlFor="interest" className="label-editorial block mb-3">Area of Interest *</label>
                <select
                  id="interest" name="interest" value={formData.interest} onChange={handleChange} required
                  className="w-full px-0 py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg font-serif text-charcoal"
                  data-testid="careers-form-interest-select"
                >
                  <option value="">Select</option>
                  {interests.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="expertise" className="label-editorial block mb-3">Expertise / About You *</label>
              <textarea
                id="expertise" name="expertise" value={formData.expertise} onChange={handleChange} required rows="4"
                placeholder="Tell us about your background and what makes you passionate..."
                className="w-full px-0 py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg text-charcoal placeholder:text-muted-warm resize-none"
                data-testid="careers-form-expertise-input"
              />
            </div>

            <div>
              <label htmlFor="message" className="label-editorial block mb-3">Your Message</label>
              <textarea
                id="message" name="message" value={formData.message} onChange={handleChange} rows="4"
                placeholder="How would you like to contribute to Behenchara?"
                className="w-full px-0 py-3 bg-transparent border-0 border-b border-charcoal focus:outline-none focus:border-burgundy text-lg text-charcoal placeholder:text-muted-warm resize-none"
                data-testid="careers-form-message-input"
              />
            </div>

            <button
              type="submit" disabled={loading}
              className="btn-burgundy px-10 py-4 inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="careers-form-submit-button"
            >
              {loading ? 'Sending' : 'Send Message'}
              <ArrowRight className="h-4 w-4" />
            </button>

            <p className="text-sm text-muted-warm">
              We'll review your application and respond within 5–7 business days.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Careers;
